import { useEffect } from "react";

/**
 * Allow native selection/copy menus on intentional UI (AI answers, form fields).
 * Book content and reader chrome stay blocked.
 */
const isNativeContextMenuAllowed = (target) => {
  if (!target || typeof target.closest !== "function") return false;
  return Boolean(
    target.closest(
      '.ai-markdown, input, textarea, [contenteditable="true"]',
    ),
  );
};

/**
 * Custom hook to disable right-click context menu and developer tools shortcuts.
 * This provides basic content protection for the reader application.
 *
 * Blocks:
 * - Right-click context menu (mouse) — except AI markdown / inputs
 * - F12 (DevTools)
 * - Ctrl+Shift+I / Cmd+Option+I (Inspect Element)
 * - Ctrl+Shift+J / Cmd+Option+J (Console)
 * - Ctrl+Shift+C / Cmd+Option+C (Inspect Element picker)
 * - Ctrl+U / Cmd+U (View Source)
 *
 * Works on both Windows and macOS.
 */
export const useDevToolsProtection = () => {
  useEffect(() => {
    // Disable right-click context menu (carve out AI text + form fields)
    const handleContextMenu = (event) => {
      if (isNativeContextMenuAllowed(event.target)) return;
      event.preventDefault();
      event.stopPropagation();
      return false;
    };

    // Disable developer tools keyboard shortcuts
    const handleKeyDown = (event) => {
      // F12 - Open DevTools
      if (event.key === "F12") {
        event.preventDefault();
        event.stopPropagation();
        return false;
      }

      // Ctrl+Shift+I (Windows/Linux) or Cmd+Option+I (Mac) - Inspect Element
      if (
        (event.ctrlKey || event.metaKey) &&
        event.shiftKey &&
        event.key.toLowerCase() === "i"
      ) {
        event.preventDefault();
        event.stopPropagation();
        return false;
      }

      // Ctrl+Shift+J (Windows/Linux) or Cmd+Option+J (Mac) - Console
      if (
        (event.ctrlKey || event.metaKey) &&
        event.shiftKey &&
        event.key.toLowerCase() === "j"
      ) {
        event.preventDefault();
        event.stopPropagation();
        return false;
      }

      // Ctrl+Shift+C (Windows/Linux) or Cmd+Option+C (Mac) - Inspect Element picker
      if (
        (event.ctrlKey || event.metaKey) &&
        event.shiftKey &&
        event.key.toLowerCase() === "c"
      ) {
        event.preventDefault();
        event.stopPropagation();
        return false;
      }

      // Ctrl+U (Windows/Linux) or Cmd+U (Mac) - View Source
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "u") {
        event.preventDefault();
        event.stopPropagation();
        return false;
      }
    };

    // Add event listeners with capture phase to catch events early
    document.addEventListener("contextmenu", handleContextMenu, true);
    document.addEventListener("keydown", handleKeyDown, true);

    // Also add to window to catch any bubbled events
    window.addEventListener("contextmenu", handleContextMenu, true);
    window.addEventListener("keydown", handleKeyDown, true);

    // Cleanup
    return () => {
      document.removeEventListener("contextmenu", handleContextMenu, true);
      document.removeEventListener("keydown", handleKeyDown, true);
      window.removeEventListener("contextmenu", handleContextMenu, true);
      window.removeEventListener("keydown", handleKeyDown, true);
    };
  }, []);
};

export default useDevToolsProtection;
